import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ManualTestCreator.css';

const ManualTestCreator = ({ 
  onSave, 
  availableNodes = [], 
  onCancel, 
  initialData = null,
  selectedNode = null,  // 👈 NEW: Currently selected node
  addPendingNode = null // 👈 NEW: Function to add test to pending queue
}) => {
  const { t } = useTranslation();
  // Form states
  const [testData, setTestData] = useState({
    title: '',
    description: '',
    targetNode: '',
    testType: 'functional',
    category: 'functional',
    priority: 'medium',
    positive: true,
    steps: []
  });

  const [currentStep, setCurrentStep] = useState({ type: 'Given', text: '' });
  const [showPreview, setShowPreview] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [editingStepIndex, setEditingStepIndex] = useState(-1); // -1 = no editing in progress

  // Function to generate translated test templates
  const getTestTemplates = () => ({
    functional: {
      title: t('manualTestCreator.templates.functional.title'),
      description: t('manualTestCreator.templates.functional.description'),
      steps: [
        { type: "Given", text: t('manualTestCreator.templates.functional.given') },
        { type: "When", text: t('manualTestCreator.templates.functional.when') },
        { type: "Then", text: t('manualTestCreator.templates.functional.then') }
      ]
    },
    security: {
      title: t('manualTestCreator.templates.security.title'),
      description: t('manualTestCreator.templates.security.description'),
      steps: [
        { type: "Given", text: t('manualTestCreator.templates.security.given') },
        { type: "When", text: t('manualTestCreator.templates.security.when') },
        { type: "Then", text: t('manualTestCreator.templates.security.then') }
      ]
    },
    integration: {
      title: t('manualTestCreator.templates.integration.title'),
      description: t('manualTestCreator.templates.integration.description'),
      steps: [
        { type: "Given", text: t('manualTestCreator.templates.integration.given') },
        { type: "When", text: t('manualTestCreator.templates.integration.when') },
        { type: "Then", text: t('manualTestCreator.templates.integration.then') }
      ]
    },
    performance: {
      title: t('manualTestCreator.templates.performance.title'),
      description: t('manualTestCreator.templates.performance.description'),
      steps: [
        { type: "Given", text: t('manualTestCreator.templates.performance.given') },
        { type: "When", text: t('manualTestCreator.templates.performance.when') },
        { type: "Then", text: t('manualTestCreator.templates.performance.then') }
      ]
    }
  });

  // État pour empêcher la réinitialisation après la première saisie
  const [hasUserInput, setHasUserInput] = useState(false);

  // Initialize with existing data if provided
  useEffect(() => {
    if (initialData) {
      setTestData({
        title: initialData.title || '',
        description: initialData.description || '',
        targetNode: initialData.targetNode || initialData.metadata?.targetNode || '',
        testType: initialData.metadata?.testType || 'functional',
        category: initialData.metadata?.category || 'functional',
        priority: initialData.metadata?.priority || 'medium',
        positive: initialData.metadata?.positive !== false,
        steps: initialData.metadata?.steps || initialData.steps || []
      });
      setHasUserInput(true); // Marquer qu'on a des données initiales
    } else if (selectedNode && !hasUserInput) {
      // 👈 FIXED: Only set defaults if user hasn't started typing yet
      setTestData(prev => ({
        ...prev,
        targetNode: selectedNode.name,
        title: `Test pour ${selectedNode.name}`,
        description: `Test automatique pour le ${selectedNode.type?.toLowerCase() || 'composant'} ${selectedNode.name}`
      }));
    }
  }, [initialData, selectedNode, hasUserInput]);

  // Validation du formulaire - appelée en temps réel
  const validateForm = () => {
    const errors = {};
    
    if (!testData.title.trim()) {
      errors.title = 'Le titre est obligatoire';
    }
    
    if (!testData.description.trim()) {
      errors.description = 'La description est obligatoire';
    }
    
    if (!testData.targetNode) {
      errors.targetNode = t('manualTestCreator.validation.targetNodeRequired');
    }
    
    if (testData.steps.length === 0) {
      errors.steps = t('manualTestCreator.validation.stepsRequired');
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Re-valider le formulaire en temps réel quand les données changent
  useEffect(() => {
    validateForm();
  }, [testData]);

  // Apply a template
  const applyTemplate = (templateKey) => {
    const testTemplates = getTestTemplates();
    const template = testTemplates[templateKey];
    if (template) {
      // 👈 NEW: Replace placeholders with the selected node name
      const nodeName = testData.targetNode || selectedNode?.name || '[Composant]';
      const nodeType = selectedNode?.type?.toLowerCase() || 'composant';
      
      const title = template.title
        .replace(/\[Composant\]/g, nodeName)
        .replace(/\[Endpoint\]/g, nodeName)
        .replace(/\[Service A\]/g, nodeName)
        .replace(/\[Service B\]/g, 'Service cible')
        .replace(/\[Fonctionnalité\]/g, nodeName);
      
      const description = template.description
        .replace(/\[Composant\]/g, nodeName)
        .replace(/\[Endpoint\]/g, nodeName)
        .replace(/\[Fonctionnalité\]/g, nodeName);

      setTestData(prev => ({
        ...prev,
        title,
        description,
        steps: [...template.steps],
        category: templateKey
      }));
    }
  };

  // Add a step
  const addStep = () => {
    if (currentStep.text.trim()) {
      if (editingStepIndex >= 0) {
        // Edit mode - save the modified step
        saveEditedStep();
      } else {
        // Add mode - add a new step
        setTestData(prev => ({
          ...prev,
          steps: [...prev.steps, { ...currentStep }]
        }));
        setCurrentStep({ type: 'Given', text: '' });
      }
    }
  };

  // Remove a step
  const removeStep = (index) => {
    setTestData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
  };

  // Move a step
  const moveStep = (index, direction) => {
    const newSteps = [...testData.steps];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < newSteps.length) {
      [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
      setTestData(prev => ({ ...prev, steps: newSteps }));
    }
  };

  // Edit a step
  const editStep = (index) => {
    const stepToEdit = testData.steps[index];
    setCurrentStep({ ...stepToEdit });
    setEditingStepIndex(index);
  };

  // Save step editing
  const saveEditedStep = () => {
    if (currentStep.text.trim() && editingStepIndex >= 0) {
      const newSteps = [...testData.steps];
      newSteps[editingStepIndex] = { ...currentStep };
      setTestData(prev => ({ ...prev, steps: newSteps }));
      setCurrentStep({ type: 'Given', text: '' });
      setEditingStepIndex(-1);
    }
  };

  // Cancel step editing
  const cancelEditStep = () => {
    setCurrentStep({ type: 'Given', text: '' });
    setEditingStepIndex(-1);
  };

  // Save the test
  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const testToSave = {
      id: initialData?.id || `manual_test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: testData.title,
      title: testData.title,
      description: testData.description,
      type: 'Test',
      nodeType: 'Test',
      targetNode: testData.targetNode,
      nodeName: testData.targetNode, // 👈 ADDED: for compatibility with GlobalTestsGraph
      author: 'Utilisateur',
      createdAt: initialData?.createdAt || new Date().toISOString(),
      steps: testData.steps, // 👈 ADDED: directly at root level for GlobalTestsGraph
      priority: testData.priority, // 👈 ADDED: directly at root level
      positive: testData.positive, // 👈 ADDED: directly at root level
      metadata: {
        testType: testData.testType,
        category: testData.category,
        priority: testData.priority,
        positive: testData.positive,
        steps: testData.steps,
        targetNode: testData.targetNode,
        isManuallyCreated: true,
        scenarios: [{
          title: testData.title,
          type: testData.category,
          positive: testData.positive,
          steps: testData.steps,
          testType: testData.positive ? 'positive' : 'negative',
          category: testData.category,
          nodeName: testData.targetNode // 👈 ADDED: for consistency
        }]
      },
      scenarios: [{
        title: testData.title,
        type: testData.category,
        positive: testData.positive,
        steps: testData.steps,
        testType: testData.positive ? 'positive' : 'negative',
        category: testData.category,
        nodeName: testData.targetNode // 👈 ADDED: for consistency
      }]
    };

    if (typeof onSave === 'function') {
      onSave(testToSave);
    }
  };

  // Send test to pending queue
  const handleSendToPending = () => {
    if (!validateForm()) {
      return;
    }

    if (!addPendingNode) {
      console.warn('⚠️ addPendingNode function not provided');
      return;
    }

    const testToAdd = {
      id: initialData?.id || `manual_test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: testData.title,
      title: testData.title,
      description: testData.description,
      type: 'Test',
      nodeType: 'Test',
      targetNode: testData.targetNode,
      nodeName: testData.targetNode,
      author: 'Utilisateur',
      createdAt: initialData?.createdAt || new Date().toISOString(),
      steps: testData.steps,
      priority: testData.priority,
      positive: testData.positive,
      isTest: true,
      metadata: {
        testType: testData.testType,
        category: testData.category,
        priority: testData.priority,
        positive: testData.positive,
        steps: testData.steps,
        targetNode: testData.targetNode,
        isManuallyCreated: true,
        scenarios: [{
          title: testData.title,
          type: testData.category,
          positive: testData.positive,
          steps: testData.steps,
          testType: testData.positive ? 'positive' : 'negative',
          category: testData.category,
          nodeName: testData.targetNode
        }]
      },
      scenarios: [{
        title: testData.title,
        type: testData.category,
        positive: testData.positive,
        steps: testData.steps,
        testType: testData.positive ? 'positive' : 'negative',
        category: testData.category,
        nodeName: testData.targetNode
      }]
    };

    // Add to pending queue
    addPendingNode(testToAdd);
    // Close the form
    onCancel();
  };

  return (
    <div className="manual-test-creator">
      <div className="manual-test-header">
        <div className="header-left">
          <h3 className="creator-title">
            <span className="title-icon">🧪</span>
            {initialData ? t('manualTestCreator.editTest') : t('manualTestCreator.createTest')}
          </h3>
          <p className="creator-subtitle">
            {t('manualTestCreator.subtitle')}
          </p>
        </div>
        <div className="header-actions">
          <button 
            className="preview-btn"
            onClick={() => setShowPreview(!showPreview)}
            title={t('manualTestCreator.testPreview')}
          >
            {showPreview ? t('manualTestCreator.edit') : t('manualTestCreator.preview')}
          </button>
        </div>
      </div>

      <div className="manual-test-body">
        {!showPreview ? (
          // Edit mode
          <div className="test-editor">
          
            {/* Informations de base */}
            <div className="basic-info-section">
              <h4 className="section-title">
                <span className="section-icon">📋</span>
                {t('nodeForm.basicInfo')}
              </h4>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    {t('manualTestCreator.testTitle')} *
                    {validationErrors.title && <span className="error-text">{validationErrors.title}</span>}
                  </label>
                  <input
                    type="text"
                    className={`form-input ${validationErrors.title ? 'error' : ''}`}
                    value={testData.title}
                    onChange={(e) => {
                      setTestData(prev => ({ ...prev, title: e.target.value }));
                      setHasUserInput(true); // Marquer que l'utilisateur a commencé à taper
                    }}
                    placeholder={t('manualTestCreator.titlePlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    {t('manualTestCreator.targetNode')} *
                    {validationErrors.targetNode && <span className="error-text">{validationErrors.targetNode}</span>}
                  </label>
                  <select
                    className={`form-select ${validationErrors.targetNode ? 'error' : ''}`}
                    value={testData.targetNode}
                    onChange={(e) => setTestData(prev => ({ ...prev, targetNode: e.target.value }))}
                  >
                    <option value="">-- Sélectionner un nœud --</option>
                    {availableNodes
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(node => (
                        <option key={node.name} value={node.name}>
                          {node.name} ({node.type})
                        </option>
                      ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Description *</label>
                  <textarea
                    className={`form-textarea ${validationErrors.description ? 'error' : ''}`}
                    value={testData.description}
                    onChange={(e) => {
                      setTestData(prev => ({ ...prev, description: e.target.value }));
                      setHasUserInput(true); // Marquer que l'utilisateur a commencé à taper
                    }}
                    placeholder={t('manualTestCreator.describeObjective')}
                    rows="3"
                  />
                  {validationErrors.description && <span className="error-text">{validationErrors.description}</span>}
                </div>
              </div>
  {/* Templates rapides */}
            <div className="templates-section">
              <h4 className="section-title">
                <span className="section-icon">🎯</span>
                {t('manualTestCreator.quickTemplates')}
                {selectedNode && (
                  <span className="selected-node-indicator">
                    - {t('manualTestCreator.selectedNode')}: {selectedNode?.name || selectedNode?.label || 'N/A'}
                  </span>
                )}
              </h4>
              <div className="templates-grid">
                {Object.entries(getTestTemplates()).map(([key, template]) => (
                  <button
                    key={key}
                    className="template-btn"
                    onClick={() => applyTemplate(key)}
                    title={template.description}
                  >
                    <span className="template-icon">
                      {key === 'functional' && '⚙️'}
                      {key === 'security' && '🔒'}
                      {key === 'integration' && '🔗'}
                      {key === 'performance' && '⚡'}
                    </span>
                    <span className="template-name">
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

              <div className="metadata-grid">
                <div className="form-group">
                  <label className="form-label">{t('manualTestCreator.category')}</label>
                  <select
                    className="form-select"
                    value={testData.category}
                    onChange={(e) => setTestData(prev => ({ ...prev, category: e.target.value }))}
                  >
                    <option value="functional">🔧 {t('manualTestCreator.categories.functional')}</option>
                    <option value="security">🔒 {t('manualTestCreator.categories.security')}</option>
                    <option value="integration">🔗 {t('manualTestCreator.categories.integration')}</option>
                    <option value="performance">⚡ {t('manualTestCreator.categories.performance')}</option>
                    <option value="exploratory">🔍 {t('manualTestCreator.categories.exploratory')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">{t('manualTestCreator.priority')}</label>
                  <select
                    className="form-select"
                    value={testData.priority}
                    onChange={(e) => setTestData(prev => ({ ...prev, priority: e.target.value }))}
                  >
                    <option value="low">🟢 {t('manualTestCreator.priorities.low')}</option>
                    <option value="medium">🟡 {t('manualTestCreator.priorities.medium')}</option>
                    <option value="high">🔴 {t('manualTestCreator.priorities.high')}</option>
                    <option value="critical">🚨 {t('manualTestCreator.priorities.critical')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">{t('manualTestCreator.testType')}</label>
                  <select
                    className="form-select"
                    value={testData.positive ? 'positive' : 'negative'}
                    onChange={(e) => setTestData(prev => ({ ...prev, positive: e.target.value === 'positive' }))}
                  >
                    <option value="positive">✅ {t('manualTestCreator.testTypes.positive')}</option>
                    <option value="negative">❌ {t('manualTestCreator.testTypes.negative')}</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Étapes Gherkin */}
            <div className="steps-section">
              <h4 className="section-title">
                <span className="section-icon">📝</span>
                 {t('stepGherkinH4')}
                {validationErrors.steps && <span className="error-text">{validationErrors.steps}</span>}
              </h4>

              {/* Liste des étapes existantes */}
              {testData.steps.length > 0 && (
                <div className="existing-steps">
                  {testData.steps.map((step, index) => (
                    <div key={index} className="step-item">
                      <div className="step-content">
                        <span className={`step-type ${step.type.toLowerCase()}`}>
                          {step.type}
                        </span>
                        <span className="step-text">{step.text}</span>
                      </div>
                      <div className="step-actions">
                        <button
                          className="step-action-btn move-up"
                          onClick={() => moveStep(index, 'up')}
                          disabled={index === 0}
                          title="Déplacer vers le haut"
                        >
                          ↑
                        </button>
                        <button
                          className="step-action-btn move-down"
                          onClick={() => moveStep(index, 'down')}
                          disabled={index === testData.steps.length - 1}
                          title="Déplacer vers le bas"
                        >
                          ↓
                        </button>
                        <button
                          className="step-action-btn edit"
                          onClick={() => editStep(index)}
                          title="Éditer cette étape"
                        >
                          ✏️
                        </button>
                        <button
                          className="step-action-btn delete"
                          onClick={() => removeStep(index)}
                          title="Supprimer cette étape"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Ajouter/Éditer une étape */}
              <div className="add-step-form">
                <h5 className="step-form-title">
                  {editingStepIndex >= 0 ? t('manualTestCreator.editStep') : t('manualTestCreator.addStep')}
                </h5>
                <div className="step-input-group">
                  <select
                    className="step-type-select"
                    value={currentStep.type}
                    onChange={(e) => setCurrentStep(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <option value="Given">Given</option>
                    <option value="When">When</option>
                    <option value="Then">Then</option>
                    <option value="And">And</option>
                    <option value="But">But</option>
                  </select>
                  <input
                    type="text"
                    className="step-text-input"
                    value={currentStep.text}
                    onChange={(e) => setCurrentStep(prev => ({ ...prev, text: e.target.value }))}
                    placeholder={t('manualTestCreator.stepPlaceholder')}
                    onKeyPress={(e) => e.key === 'Enter' && addStep()}
                  />
                  <button
                    className="add-step-btn"
                    onClick={addStep}
                    disabled={!currentStep.text.trim()}
                  >
                    {editingStepIndex >= 0 ? t('manualTestCreator.save') : t('manualTestCreator.add')}
                  </button>
                  {editingStepIndex >= 0 && (
                    <button
                      className="cancel-edit-btn"
                      onClick={cancelEditStep}
                      title={t('actions.cancel')}
                    >
                      ❌ {t('actions.cancel')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Preview mode
          <div className="test-preview">
            <div className="preview-content">
              <div className="preview-header">
                <h3 className="preview-title">{testData.title || 'Titre non défini'}</h3>
                <div className="preview-badges">
                  <span className={`category-badge ${testData.category}`}>
                    {testData.category}
                  </span>
                  <span className={`priority-badge ${testData.priority}`}>
                    {testData.priority}
                  </span>
                  <span className={`type-badge ${testData.positive ? 'positive' : 'negative'}`}>
                    {testData.positive ? 'Positif' : 'Négatif'}
                  </span>
                </div>
              </div>

              <div className="preview-description">
                <h4>Description</h4>
                <p>{testData.description || 'Aucune description'}</p>
              </div>

              <div className="preview-target">
                <h4>{t('featureManager.targetNode')}</h4>
                <p>{testData.targetNode || t('featureManager.noNodeSelected')}</p>
              </div>

              <div className="preview-steps">
                <h4>Étapes ({testData.steps.length})</h4>
                {testData.steps.length > 0 ? (
                  <div className="steps-list">
                    {testData.steps.map((step, index) => (
                      <div key={index} className="preview-step">
                        <span className={`preview-step-type ${step.type.toLowerCase()}`}>
                          {step.type}
                        </span>
                        <span className="preview-step-text">{step.text}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-steps">Aucune étape définie</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="manual-test-footer">
        <div className="footer-left">
          <span className="validation-status">
            {Object.keys(validationErrors).length > 0 && (
              <span className="validation-errors">
                {t('manualTestCreator.validationErrorsCount', { count: Object.keys(validationErrors).length })}
              </span>
            )}
          </span>
        </div>
        <div className="footer-actions">
          <button 
            className="cancel-btn"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </button>
          {addPendingNode && (
            <button 
              className="pending-btn"
              onClick={handleSendToPending}
              disabled={Object.keys(validationErrors).length > 0}
              title={t('manualTestCreator.sendToPending')}
            >
              {t('manualTestCreator.sendToPending')}
            </button>
          )}
          <button 
            className="save-btn"
            onClick={handleSave}
            disabled={Object.keys(validationErrors).length > 0}
          >
            {initialData ? t('manualTestCreator.update') : t('manualTestCreator.createTestAction')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualTestCreator;
